import './Products.scss'

import { Alert, Image, message, Tooltip } from 'antd'
import { getFormattedPrice } from 'helpers/Formatting'
import { useEffect } from 'react'

import { useSiparisDetayQuery } from '../../gql/GeneratedGraphqlTypes'
import { ReactComponent as Hazirlaniyor } from '../../images/status-icons/hazirlaniyor.svg'
import { ReactComponent as Iade } from '../../images/status-icons/iade.svg'
import { ReactComponent as Iptal } from '../../images/status-icons/iptalTalep.svg'
import { ReactComponent as Kargo } from '../../images/status-icons/kargo.svg'
import { ReactComponent as TeslimIcon } from '../../images/status-icons/teslim.svg'
import { ProductsItem } from './types'

interface Props {
  siparis: string
}

const rowClassName = (record: any, index: number) => {
  return record.durum === 'İptal' ? 'odd-row-product' : 'even-row-product'
}
const Products = ({ siparis }: Props) => {
  const temp = parseInt(siparis)
  const [messageApi, contextHolder] = message.useMessage()
  const isMobile = window.innerWidth <= 768
  const { data, error } = useSiparisDetayQuery({
    variables: { siparis_id: temp },
  })
  useEffect(() => {
    if (error) {
      messageApi.error(`${error}`, 2)
    }
  }, [error, messageApi])
  if (!data) {
    return null
  }

  const renderIcon = (status: string) => {
    switch (status) {
      case 'Hazırlanıyor':
        return <Hazirlaniyor className="status-icon" />
      case 'Teslim Edildi':
        return <TeslimIcon className="status-icon" />
      case 'Kargolandı':
        return <Kargo className="status-icon" />
      case 'İade Edildi':
        return <Iade className="status-icon" />
      case 'İptal Talebi':
        return <Iptal className="status-icon" />
      default:
        return null // Eğer durum tanımlanmamışsa hiçbir şey döndürme
    }
  }

  const ProductsItems: ProductsItem[] =
    data?.siparisDetay.satirlar?.map((urun) => {
      const adet = urun?.adet ?? 0 // Null veya undefined ise 0 kabul edelim.
      const kalan_adet = urun?.durum === 'Hazırlanıyor' ? (urun?.adet ?? 0) : (urun?.kalan_adet ?? 0)
      const birim_fiyat = parseFloat(urun?.birim_fiyat ?? '0') // Null veya undefined ise 0 kabul edelim ve string olarak gelen veriyi number'a çevirelim.
      const kdv_dahil_fiyat = parseFloat(urun?.birim_fiyat ?? '0') * adet
      const imageUrl = urun?.urun.resimler || [] // Resim URL'si, eğer yoksa boş dizi
      // Check if the product is marked as "Dinamik Koli Ürünü"
      const isDynamicPackage = urun?.urun.bilgiler?.some(
        (bilgi) => bilgi?.isim === 'Dinamik Koli Ürünü' && bilgi.deger === 'True',
      )

      return {
        key: urun?.id?.toString() || '', //Gerekli
        urun_isim: urun?.urun.isim || '',
        aciklamalar: urun?.urun.aciklamalar || '',
        adet: adet,
        images: imageUrl || '', // Resim URL'si, eğer yoksa boş string
        kalan_adet: kalan_adet,
        birim_fiyat: `${getFormattedPrice(String(birim_fiyat))} ₺`,
        kdv_dahil_fiyat: `${getFormattedPrice(String(kdv_dahil_fiyat))} ₺`,
        durum: isDynamicPackage ? 'Set' : urun?.durum || '',
      }
    }) || []

  return (
    <>
      {contextHolder}
      <div>
        <Alert
          className="custom-alert"
          description="Almış olduğunuz bütün ürünler listelenmektedir. "
          type="success"
          showIcon
        />
        <br />
        {isMobile ? (
          <div className="order-detail-products-container-mobile">
            {ProductsItems.map((urun) => (
              <div key={urun.key} className={`product-item ${rowClassName(urun, 0)}`}>
                <div className="left-side">
                  <Image
                    src={urun.images[0] ?? ''}
                    alt="image"
                    className="product-image"
                    width={72}
                    height={72}
                  />
                  <div className="product-name-wrapper">
                    <span className="product-name">{urun.urun_isim}</span>
                    {urun.aciklamalar && <span className="product-aciklama"><span className="aciklama-label">Açıklama:</span> {urun.aciklamalar}</span>}
                  </div>
                </div>
                <div className="right-side">
                  <p className="info">
                    <span>Adet:</span>
                    {urun.adet}
                  </p>
                  <p className="info">
                    <span>Kalan Adet: </span>
                    {urun.kalan_adet}
                  </p>
                  <p className="info">
                    <span>KDV Dahil Birim Fiyat: </span>
                    {urun.birim_fiyat} TL
                  </p>
                  <p className="info">
                    <span>KDV Dahil Tutar:</span>
                    {urun.kdv_dahil_fiyat} TL
                  </p>
                  <div className="product-status info">
                    <span>Durum: </span>
                    {renderIcon(urun.durum)}
                    {urun.durum}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="order-detail-products-container">
            <div className="product-item-header">
              <span className="name">Ürün Adı</span>
              <span>Adet</span>
              <span>Kalan Adet</span>
              <span>KDV Dahil Birim Fiyat</span>
              <span>KDV Dahil Tutar</span>
              <span>Durum</span>
            </div>
            {ProductsItems.map((urun) => (
              <div key={urun.key} className={`product-item ${rowClassName(urun, 0)}`}>
                <div className="left-side">
                  <Image src={urun.images[0] ?? ''} alt="image" className="product-image" width={72} height={72} />
                  <div className="product-name-wrapper">
                    <Tooltip title={urun.urun_isim} placement="top">
                      <span className="product-name">
                        {urun.urun_isim.length > 45 ? `${urun.urun_isim.slice(0, 45)}...` : urun.urun_isim}
                      </span>
                    </Tooltip>
                    {urun.aciklamalar && <span className="product-aciklama"><span className="aciklama-label">Açıklama:</span> {urun.aciklamalar}</span>}
                  </div>
                </div>
                <p className="quantity">{urun.adet}</p>
                <p>{urun.kalan_adet}</p>
                <p>{urun.birim_fiyat}</p>
                <p>{urun.kdv_dahil_fiyat}</p>
                <div className="product-status">
                  {renderIcon(urun.durum)}
                  {urun.durum}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default Products
